import React, { useContext } from 'react';
import { Button, Col, Form, FormInstance, Grid, Row } from 'antd';

import { DeleteOutlined } from '@ant-design/icons';

import { ContractTypeEnum, LanguageLevelEnum, LevelEnum } from '../common/types';
import { useGetAllUsers } from '../common/useGetAllUsers';
import { useGetCitiesDictionary } from '../common/useGetCitiesDictionary';
import { useGetRecommendersDictionary } from '../common/useGetRecommendersDictionary';
import { useGetDictionaryByName } from '../features/dictionaries/useGetDictionaryByName';

import { FormCheckboxGroup } from './forms/FormCheckboxGroup';
import { FormDoubleInput } from './forms/FormDoubleInput';
import { FormInput } from './forms/FormInput';
import { FormLinkInput } from './forms/FormLinkInput';
import { FormMonthPicker } from './forms/FormMonthPicker';
import { FormSelect } from './forms/FormSelect';
import { FormSelectMultiple } from './forms/FormSelectMultiple';
import { FormSelectOrAddItem } from './forms/FormSelectOrAddItem';
import { FormTextArea } from './forms/FormTextArea';
import { CandidateDuplicatesContext } from './CandidateDuplicatesContext';
import { CandidateDuplicatesTable } from './CandidateDuplicatesTable';
import { StyledColumn } from './StyledColumn';
import { AddIcon } from './TableWithDataUpdate';

export const mapErrorFieldsToFormLabels = {
  email: 'E-mail',
  expectedSalary: 'Salary description',
};

export type CandidateStartingNoteFormProps = {
  form: FormInstance;
};

export const CandidateStartingNoteForm: React.FC<CandidateStartingNoteFormProps> = ({ form }) => {
  return (
    <Form form={form} style={{ flexGrow: '1' }} layout="vertical" colon={false}>
      <FormTextArea rows={10} name="note" label="Note" />
    </Form>
  );
};

export type CandidateDataFormProps = {
  form?: FormInstance;
};

export const CandidateAdditionalDataForm: React.FC<CandidateDataFormProps> = ({ form }) => {
  const screens = Grid.useBreakpoint();
  const contractTypesOptions = Object.values(ContractTypeEnum);

  const { data: technologies } = useGetDictionaryByName('technologies');
  const technologyOptions = (technologies ?? []).map((technology) => ({
    content: technology.value,
    value: technology.value,
  }));

  const { data: recommenders, isLoading: recommendersLoading } = useGetRecommendersDictionary();
  const recommendersOptions =
    recommenders && !recommendersLoading
      ? recommenders.map((recommender) => ({ value: recommender.value, content: recommender.value }))
      : [];

  const { data: sources, isLoading: sourcesLoading } = useGetDictionaryByName('candidate-source');
  const sourcesOptions =
    sources && !sourcesLoading ? sources.map((source) => ({ value: source.value, content: source.value })) : [];

  const { data: languages, isLoading: languagesLoading } = useGetDictionaryByName('languages');
  const languageOptions =
    languages && !languagesLoading
      ? languages.map((language) => ({ value: language.value, content: language.value }))
      : [];

  const seniorityOptions = Object.entries(LevelEnum).map((level) => ({
    value: level[0],
    content: level[1],
  }));

  const languageLevelOptions = Object.entries(LanguageLevelEnum)
    .filter(([key]) => key !== 'UNKNOWN')
    .map((level) => ({
      value: level[0],
      content: level[1],
    }));

  return (
    <Form form={form} style={{ flexGrow: '1' }} layout="vertical" colon={false}>
      <StyledColumn gap="0.3rem">
        <Row gutter={[10, 5]} wrap>
          <Col xs={24} sm={12}>
            <FormSelect name="recommendedBy" label="Recommended by" options={recommendersOptions} />
          </Col>
          <Col xs={24} sm={12}>
            <FormSelect name="source" label="Source" options={sourcesOptions} />
          </Col>
        </Row>

        <Row gutter={[10, 5]} wrap>
          <Col xs={24} sm={12}>
            <FormMonthPicker name="professionalExperienceSince" label="Professional experience since" />
          </Col>
          <Col xs={24} sm={12}>
            <FormSelect name="seniority" label="Seniority" options={seniorityOptions} />
          </Col>
        </Row>

        <FormSelectMultiple name="technologies" label="Technologies" mode="tags" options={technologyOptions} />

        <Form.List name="languages">
          {(fields, { add, remove }) => (
            <StyledColumn gap="0.3rem">
              {fields.map((field) => (
                <Row key={field.key} gutter={[10, 5]} wrap align="bottom">
                  <Col xs={24} md={11}>
                    <FormSelectOrAddItem
                      name={[field.name, 'language']}
                      label="Language"
                      options={languageOptions}
                      form={form}
                      rules={[{ required: true, message: 'Language is required' }]}
                    />
                  </Col>
                  <Col xs={24} md={9}>
                    <FormSelect
                      name={[field.name, 'level']}
                      label="Level"
                      options={languageLevelOptions}
                      rules={[{ required: true, message: 'Level is required' }]}
                    />
                  </Col>
                  <Col xs={24} md={4}>
                    <Button danger type="text" icon={<DeleteOutlined />} onClick={() => remove(field.name)}>
                      Remove
                    </Button>
                  </Col>
                </Row>
              ))}

              <Button type="dashed" icon={<AddIcon />} onClick={() => add()}>
                Add language
              </Button>
            </StyledColumn>
          )}
        </Form.List>

        <Row gutter={[10, 5]} wrap>
          <Col xs={24} sm={8}>
            <FormInput name="createdDate" label="Created date" disabled />
          </Col>
          <Col xs={24} sm={8}>
            <FormInput name="createdBy" label="Created by" disabled />
          </Col>
          <Col xs={24} sm={8}>
            <FormInput name="workMode" label="Work mode" />
          </Col>
        </Row>

        <FormCheckboxGroup
          name="contractTypes"
          label="Supported contract"
          options={contractTypesOptions}
          vertical={screens.xs || (screens.sm && !screens.md)}
        />
      </StyledColumn>
    </Form>
  );
};

export const CandidateDataForm: React.FC<CandidateDataFormProps> = ({ form }) => {
  const { data: cities, isLoading: citiesLoading } = useGetCitiesDictionary();
  const cityOptions =
    cities && !citiesLoading ? cities.map((city) => ({ value: city.value, content: city.value })) : [];

  const { data: usersData, isLoading: usersLoading } = useGetAllUsers();
  const userOptions =
    !usersLoading && usersData
      ? usersData.results.map((user) => ({
          value: user.id,
          content: `${user.firstName} ${user.lastName}`,
        }))
      : [];

  const { setDuplicatesSearchData } = useContext(CandidateDuplicatesContext);

  const onBlur = () => {
    const firstName = form?.getFieldValue('firstName');
    const lastName = form?.getFieldValue('lastName');
    setDuplicatesSearchData({ firstName, lastName });
  };

  return (
    <Form form={form} style={{ flexGrow: '1' }} layout="vertical" colon={false}>
      <StyledColumn gap="0.3rem">
        <Row gutter={[10, 5]} wrap>
          <Col xs={24} md={6}>
            <FormInput name="firstName" label="Candidate first name" required onBlur={onBlur} />
          </Col>
          <Col xs={24} md={6}>
            <FormInput name="lastName" label="Candidate last name" required onBlur={onBlur} />
          </Col>
          <Col xs={24} md={6}>
            <FormLinkInput name="linkedinProfile" label="LinkedIn" />
          </Col>
          <Col xs={24} md={6}>
            <FormSelect name="recruiter" label="Assigned recruiter" options={userOptions} />
          </Col>
        </Row>

        <Row gutter={[10, 5]} wrap>
          <Col xs={24} sm={8}>
            <FormInput name="email" label="E-mail" />
          </Col>
          <Col xs={24} sm={8}>
            <FormInput name="phoneNumber" label="Phone" />
          </Col>
          <Col xs={24} sm={8}>
            <FormInput name="appliedPosition" label="Applied position" required />
          </Col>
        </Row>

        <CandidateDuplicatesTable />

        <Row gutter={[10, 5]} wrap>
          <Col xs={24} sm={8}>
            <FormInput name="githubProfile" label="Github/Gitlab" />
          </Col>
          <Col xs={24} sm={8}>
            <FormInput name="additionalLink" label="Additional link" />
          </Col>
          <Col xs={24} sm={8}>
            <FormSelectOrAddItem name="city" label="City" options={cityOptions} form={form} />
          </Col>
        </Row>

        <FormInput name="expectedSalary" label="Salary description" />

        <FormDoubleInput
          leftInput={<FormTextArea rows={1} name="totalEmploymentCost" label="Total emplyment cost" />}
          rightInput={<FormInput name="noticePeriod" label="Notice period" />}
        />

        <FormTextArea rows={4} name="summary" label="Summary" />
      </StyledColumn>
    </Form>
  );
};
